import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    Plus, Droplets, ArrowRight, ArrowLeft, Clock, CheckCircle
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Lavanderia = () => {
    const [registros, setRegistros] = useState([]);
    const [cortes, setCortes] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [loading, setLoading] = useState(true);
    const [envioModalOpen, setEnvioModalOpen] = useState(false);
    const [retornoModalOpen, setRetornoModalOpen] = useState(false);
    const [selectedRegistro, setSelectedRegistro] = useState(null);

    const [envioForm, setEnvioForm] = useState({
        corte_id: '',
        lote_id: '',
        lavanderia_id: '',
        lavanderia_nombre: '',
        responsable: '',
        fecha_envio: new Date().toISOString().split('T')[0],
        cantidad_enviada: 0,
        entrega_nombre: '',
        observaciones: ''
    });

    const [retornoForm, setRetornoForm] = useState({
        lavanderia_id: '',
        fecha_regreso: new Date().toISOString().split('T')[0],
        cantidad_recibida: 0,
        recibe_nombre: '',
        observaciones: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [lavanderiaRes, cortesRes, empRes] = await Promise.all([
                axios.get(`${API_URL}/api/lavanderia`),
                axios.get(`${API_URL}/api/cortes`),
                axios.get(`${API_URL}/api/empleados`)
            ]);
            setRegistros(lavanderiaRes.data);
            setCortes(cortesRes.data);
            setEmpleados(empRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const lavanderos = empleados.filter(e => 
        e.tipo_trabajador === 'lavanderia' || e.puesto?.toLowerCase().includes('lavand')
    );

    const handleLavanderiaChange = (empId) => {
        const emp = empleados.find(e => e.id === empId);
        setEnvioForm(prev => ({
            ...prev,
            lavanderia_id: empId,
            lavanderia_nombre: emp ? emp.nombre : ''
        }));
    };

    const handleCorteChange = (corteId) => {
        const corte = cortes.find(c => c.id === corteId);
        setEnvioForm(prev => ({
            ...prev,
            corte_id: corteId,
            lote_id: corte ? corte.lote_id : ''
        }));
    };

    const handleEnvioSubmit = async (e) => {
        e.preventDefault();
        if (!envioForm.corte_id || !envioForm.lavanderia_id || !envioForm.responsable || !envioForm.cantidad_enviada || !envioForm.entrega_nombre) {
            toast.error('Por favor complete los campos requeridos');
            return;
        }

        try {
            await axios.post(`${API_URL}/api/lavanderia`, envioForm);
            toast.success('Envío registrado exitosamente');
            setEnvioModalOpen(false);
            setEnvioForm({
                corte_id: '', lote_id: '', lavanderia_id: '', lavanderia_nombre: '',
                responsable: '', fecha_envio: new Date().toISOString().split('T')[0],
                cantidad_enviada: 0, entrega_nombre: '', observaciones: ''
            });
            fetchData();
        } catch (error) {
            console.error('Error saving envio:', error);
            toast.error(error.response?.data?.detail || 'Error al registrar envío');
        }
    };

    const handleRetornoSubmit = async (e) => {
        e.preventDefault();
        if (!retornoForm.cantidad_recibida || !retornoForm.recibe_nombre) {
            toast.error('Por favor complete los campos requeridos');
            return;
        }

        try {
            await axios.post(`${API_URL}/api/lavanderia/retorno`, retornoForm);
            toast.success('Retorno registrado exitosamente');
            setRetornoModalOpen(false);
            setSelectedRegistro(null);
            fetchData();
        } catch (error) {
            console.error('Error saving retorno:', error);
            toast.error(error.response?.data?.detail || 'Error al registrar retorno');
        }
    };

    const openRetornoModal = (registro) => {
        setSelectedRegistro(registro);
        setRetornoForm({
            lavanderia_id: registro.id,
            fecha_regreso: new Date().toISOString().split('T')[0],
            cantidad_recibida: registro.cantidad_enviada,
            recibe_nombre: '',
            observaciones: ''
        });
        setRetornoModalOpen(true);
    };

    const getCorteRelacion = (corteId) => {
        const corte = cortes.find(c => c.id === corteId);
        return corte ? corte.relacion_corte : '-';
    };

    const enProceso = registros.filter(r => r.estado === 'en_proceso');
    const completados = registros.filter(r => r.estado === 'completado');

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fadeIn" data-testid="lavanderia-page">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-zinc-100" >
                        Lavandería, Planchado y Terminación
                    </h1>
                    <p className="text-zinc-400 mt-1">Control de envíos y retornos de lavandería</p>
                </div>
                <Button
                    onClick={() => {
                        setEnvioForm({
                            corte_id: '', lote_id: '', lavanderia_id: '', lavanderia_nombre: '',
                            responsable: '', fecha_envio: new Date().toISOString().split('T')[0],
                            cantidad_enviada: 0, entrega_nombre: '', observaciones: ''
                        });
                        setEnvioModalOpen(true);
                    }}
                    className="bg-amber-400 hover:bg-amber-500 text-zinc-900"
                    data-testid="add-envio-btn"
                >
                    <Plus className="w-5 h-5 mr-2" />
                    Nuevo Envío
                </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Card className="card">
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-amber-400/10 flex items-center justify-center">
                            <Clock className="w-6 h-6 text-amber-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-zinc-100">{enProceso.length}</p>
                            <p className="text-sm text-zinc-400">En Proceso</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card">
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-emerald-400/10 flex items-center justify-center">
                            <CheckCircle className="w-6 h-6 text-emerald-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-zinc-100">{completados.length}</p>
                            <p className="text-sm text-zinc-400">Completados</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* En Proceso */}
            <Card className="card">
                <div className="p-4 border-b border-zinc-700">
                    <h3 className="font-semibold text-zinc-100 flex items-center gap-2">
                        <Droplets className="w-5 h-5 text-amber-400" />
                        Envíos en Proceso
                    </h3>
                </div>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-zinc-800">
                                    <TableHead className="table-header">Fecha Envío</TableHead>
                                    <TableHead className="table-header">Corte</TableHead>
                                    <TableHead className="table-header">Lavandería</TableHead>
                                    <TableHead className="table-header">Responsable</TableHead>
                                    <TableHead className="table-header">Cantidad</TableHead>
                                    <TableHead className="table-header text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {enProceso.length > 0 ? enProceso.map((registro) => (
                                    <TableRow key={registro.id} className="table-row" data-testid={`lavanderia-row-${registro.id}`}>
                                        <TableCell className="table-cell">{registro.fecha_envio}</TableCell>
                                        <TableCell className="table-cell font-medium">{getCorteRelacion(registro.corte_id)}</TableCell>
                                        <TableCell className="table-cell">{registro.lavanderia_nombre || '-'}</TableCell>
                                        <TableCell className="table-cell">{registro.responsable}</TableCell>
                                        <TableCell className="table-cell">
                                            <span className="badge-info">{registro.cantidad_enviada} pzas</span>
                                        </TableCell>
                                        <TableCell className="table-cell text-right">
                                            <Button
                                                size="sm"
                                                onClick={() => openRetornoModal(registro)}
                                                className="bg-emerald-500 hover:bg-emerald-600"
                                                data-testid={`registrar-retorno-lav-${registro.id}`}
                                            >
                                                <ArrowLeft className="w-4 h-4 mr-1" />
                                                Retorno
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-8 text-zinc-400">
                                            No hay envíos en proceso
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Completados */}
            <Card className="card">
                <div className="p-4 border-b border-zinc-700">
                    <h3 className="font-semibold text-zinc-100 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-400" />
                        Historial Completado
                    </h3>
                </div>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-zinc-800">
                                    <TableHead className="table-header">Fecha Envío</TableHead>
                                    <TableHead className="table-header">Fecha Retorno</TableHead>
                                    <TableHead className="table-header">Corte</TableHead>
                                    <TableHead className="table-header">Lavandería</TableHead>
                                    <TableHead className="table-header">Enviado</TableHead>
                                    <TableHead className="table-header">Recibido</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {completados.length > 0 ? completados.map((registro) => (
                                    <TableRow key={registro.id} className="table-row">
                                        <TableCell className="table-cell">{registro.fecha_envio}</TableCell>
                                        <TableCell className="table-cell">{registro.fecha_regreso || '-'}</TableCell>
                                        <TableCell className="table-cell font-medium">{getCorteRelacion(registro.corte_id)}</TableCell>
                                        <TableCell className="table-cell">{registro.lavanderia_nombre || '-'}</TableCell>
                                        <TableCell className="table-cell">{registro.cantidad_enviada}</TableCell>
                                        <TableCell className="table-cell">
                                            <span className={registro.cantidad_recibida === registro.cantidad_enviada ? 'badge-success' : 'badge-warning'}>
                                                {registro.cantidad_recibida || '-'}
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-8 text-zinc-400">
                                            No hay registros completados
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Envio Modal */}
            <Dialog open={envioModalOpen} onOpenChange={setEnvioModalOpen}>
                <DialogContent className="sm:max-w-[550px]" data-testid="envio-modal">
                    <DialogHeader>
                        <DialogTitle >
                            <ArrowRight className="w-5 h-5 inline mr-2 text-amber-400" />
                            Nuevo Envío a Lavandería
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleEnvioSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <Label className="form-label">Fecha de Envío *</Label>
                                <Input
                                    type="date"
                                    value={envioForm.fecha_envio}
                                    onChange={(e) => setEnvioForm({ ...envioForm, fecha_envio: e.target.value })}
                                    data-testid="envio-fecha"
                                />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Cantidad *</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    value={envioForm.cantidad_enviada}
                                    onChange={(e) => setEnvioForm({ ...envioForm, cantidad_enviada: parseInt(e.target.value) || 0 })}
                                    data-testid="envio-cantidad"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <Label className="form-label">Corte Relacionado *</Label>
                            <Select value={envioForm.corte_id} onValueChange={handleCorteChange}>
                                <SelectTrigger data-testid="envio-corte">
                                    <SelectValue placeholder="Seleccionar corte" />
                                </SelectTrigger>
                                <SelectContent>
                                    {cortes.map((c) => (
                                        <SelectItem key={c.id} value={c.id}>{c.relacion_corte} - {c.marca}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="form-group">
                            <Label className="form-label">Lavandería / Servicio *</Label>
                            <Select value={envioForm.lavanderia_id} onValueChange={handleLavanderiaChange}>
                                <SelectTrigger data-testid="envio-lavanderia">
                                    <SelectValue placeholder="Seleccionar lavandería" />
                                </SelectTrigger>
                                <SelectContent>
                                    {(lavanderos.length > 0 ? lavanderos : empleados).map(e => (
                                        <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <Label className="form-label">Responsable *</Label>
                                <Input
                                    value={envioForm.responsable}
                                    onChange={(e) => setEnvioForm({ ...envioForm, responsable: e.target.value })}
                                    placeholder="Nombre del responsable"
                                    data-testid="envio-responsable"
                                />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Quién Entrega *</Label>
                                <Input
                                    value={envioForm.entrega_nombre}
                                    onChange={(e) => setEnvioForm({ ...envioForm, entrega_nombre: e.target.value })}
                                    placeholder="Nombre de quien entrega"
                                    data-testid="envio-entrega"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <Label className="form-label">Observaciones</Label>
                            <Textarea
                                value={envioForm.observaciones}
                                onChange={(e) => setEnvioForm({ ...envioForm, observaciones: e.target.value })}
                                placeholder="Notas adicionales"
                                rows={2}
                                data-testid="envio-observaciones"
                            />
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setEnvioModalOpen(false)}>
                                Cancelar
                            </Button>
                            <Button type="submit" className="bg-amber-400 hover:bg-amber-500 text-zinc-900" data-testid="save-envio-btn">
                                Registrar Envío
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Retorno Modal */}
            <Dialog open={retornoModalOpen} onOpenChange={setRetornoModalOpen}>
                <DialogContent className="sm:max-w-[500px]" data-testid="retorno-lav-modal">
                    <DialogHeader>
                        <DialogTitle >
                            <ArrowLeft className="w-5 h-5 inline mr-2 text-emerald-400" />
                            Registrar Retorno de Lavandería
                        </DialogTitle>
                    </DialogHeader>
                    {selectedRegistro && (
                        <div className="bg-zinc-800 p-3 rounded-lg mb-4">
                            <p className="text-sm text-zinc-400">Corte: <span className="font-medium text-zinc-100">{getCorteRelacion(selectedRegistro.corte_id)}</span></p>
                            <p className="text-sm text-zinc-400">Cantidad enviada: <span className="font-medium text-zinc-100">{selectedRegistro.cantidad_enviada}</span></p>
                        </div>
                    )}
                    <form onSubmit={handleRetornoSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <Label className="form-label">Fecha de Retorno *</Label>
                                <Input
                                    type="date"
                                    value={retornoForm.fecha_regreso}
                                    onChange={(e) => setRetornoForm({ ...retornoForm, fecha_regreso: e.target.value })}
                                    data-testid="retorno-lav-fecha"
                                />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Cantidad Recibida *</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    value={retornoForm.cantidad_recibida}
                                    onChange={(e) => setRetornoForm({ ...retornoForm, cantidad_recibida: parseInt(e.target.value) || 0 })}
                                    data-testid="retorno-lav-cantidad"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <Label className="form-label">Quién Recibe *</Label>
                            <Input
                                value={retornoForm.recibe_nombre}
                                onChange={(e) => setRetornoForm({ ...retornoForm, recibe_nombre: e.target.value })}
                                placeholder="Nombre de quien recibe"
                                data-testid="retorno-lav-recibe"
                            />
                        </div>

                        <div className="form-group">
                            <Label className="form-label">Observaciones</Label>
                            <Textarea
                                value={retornoForm.observaciones}
                                onChange={(e) => setRetornoForm({ ...retornoForm, observaciones: e.target.value })}
                                placeholder="Notas adicionales"
                                rows={2}
                                data-testid="retorno-lav-observaciones"
                            />
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setRetornoModalOpen(false)}>
                                Cancelar
                            </Button>
                            <Button type="submit" className="bg-emerald-500 hover:bg-emerald-600" data-testid="save-retorno-lav-btn">
                                Registrar Retorno
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};
